import $ from 'jquery';

interface NewCommentOptions {
  target: string;
  isReply: false;
  privateSelector: string;
}

interface ReplyCommentOptions {
  target: string;
  isReply: true;
  parentIsPrivate: boolean;
}

type CommentFormOptions = NewCommentOptions | ReplyCommentOptions;

export enum AccessLevel {
  None = 'none',
  Public = 'public',
  Private = 'private',
}

type Mention = {
  id: string;
  name: string;
  access: AccessLevel;
};

export default class IdeaCommentForm {
  private readonly mentions: Map<string, Mention>;
  private mentionPrefix: string = '';
  private $commentForm: JQuery;
  private $commentBody: JQuery;
  private $privateComment: JQuery;
  private _errorContainer: JQuery;
  private readonly isPrivateComment: JQuery;
  private errorMessage: string | null = null;
  private hasErrors: boolean = false;
  private target: string;
  private isReply: boolean;
  private privateSelector: string | null;
  private parentIsPrivate: boolean;

  constructor() {
    this.mentions = new Map();
  }

  /**
   *
   * @param options.target - The selector for the comment form
   * @param options.isReply - Whether this is a reply or a new comment
   * @param options.privateSelector - The selector for the checkbox in new comments that determines whether the comment is private
   * @param options.parentIsPrivate - When it is a reply, whether the parent comment is private
   */
  init(options: CommentFormOptions) {
    this.target = options.target;
    this.isReply = options.isReply;

    if (this.isReply) {
      const newOptions = options as ReplyCommentOptions;
      this.privateSelector = null;
      this.parentIsPrivate = newOptions.parentIsPrivate;
    } else {
      const newOptions = options as NewCommentOptions;
      this.privateSelector = newOptions.privateSelector;
      this.parentIsPrivate = false;
    }

    this.initElements();
    this.bindEvents();
  }

  initElements() {
    this.$commentForm = $(this.target);
    this.$commentBody = this.$commentForm.find('.full-body');
    if (this.privateSelector) {
      this.$privateComment = $(this.privateSelector);
    }
  }

  bindEvents() {
    $(this.$commentForm).on('ajax:error', this.handleCommentError.bind(this));

    if (this.privateSelector) {
      $(document).on('change', this.privateSelector, () => {
        this.render();
      });
    }

    $(this.$commentForm).on('editor:mention', this.handleMention.bind(this));
  }

  handleCommentError(event: JQuery.Event, xhr: JQuery.jqXHR) {
    const errors = xhr.responseJSON && xhr.responseJSON.errors;

    let message = '';
    if (errors && errors.editor_content) {
      message =
        'The comment cannot be blank. Please try again with text included in the comment.';
    } else {
      message = 'There was an error saving your comment. Please try again.';
    }

    this.errorMessage = message;
    this.hasErrors = true;

    this.render();
  }

  /**
   * @param prefix comes from the backend to enable translations
   * Its used to prefix the mention names in the error message, will be escaped for HTML
   */
  handleMention(event, mention: Mention, prefix: string) {
    event.stopPropagation();
    event.preventDefault();

    this.mentionPrefix = prefix;
    this.mentions.set(mention.id, mention);

    this.errorMessage = null;

    this.render();
  }

  get $errorContainer() {
    if (!this._errorContainer || this._errorContainer.length === 0) {
      this._errorContainer = $(
        `<div class='errors' style='margin: 5px 0 10px'>
          <div class='help-inline below' style='margin-top: 5px;'></div>
        </div>`
      );
      this.$commentBody.append(this._errorContainer);
    }

    return this._errorContainer;
  }

  getInvalidMentions() {
    return [...this.mentions.values()].filter(mention => {
      const newCommentAndPrivate =
        this.$privateComment && this.$privateComment.is(':checked');
      const replyAndPrivate = this.isReply && this.parentIsPrivate;

      if (newCommentAndPrivate || replyAndPrivate) {
        return mention.access !== AccessLevel.Private;
      } else {
        return !mention.access || mention.access === AccessLevel.None;
      }
    });
  }

  render() {
    this.initElements();

    const invalidMentions = this.getInvalidMentions();

    this.$commentBody.toggleClass(
      'field-with-errors',
      Boolean(invalidMentions.length > 0 || this.hasErrors)
    );

    const $helpInline = this.$errorContainer.find('.help-inline');

    $helpInline.html('');

    if (this.errorMessage) {
      $helpInline.text(this.errorMessage);
    } else if (invalidMentions.length > 0) {
      const prefix = $(`<span></span>`);
      prefix.text(this.mentionPrefix);

      const names = $(`<span style='font-weight: bold;'></span>`);
      names.text(invalidMentions.map(m => m.name).join(', '));
      $helpInline.append(prefix);
      $helpInline.append(names);
    }
  }
}
